import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../../utils/APIClient';
import {isOk} from '../../../utils/apiResponse';
import {API_URL} from '../../../settings/local_settings';
import {accountsPathFor} from '../../agency/connections_v2/platformsModel';
import {accessLabel, connectedByText, platformUserText, unitCountText} from '../../agency/connections_v2/platformsText';
import {HELP_ACCOUNT_NOT_LISTED, LinkToArticle} from '../../help_center/LinkToArticle';
import {connectionsOfChannel, instructionsFor, personText} from './missingAccountModel';
import {useAccountDiscovery} from './useAccountDiscovery';

const NS = 'control_central.link_account_missing';
const RAW = {escapeValue: false};

/**
 * «¿No encuentras la cuenta?» del modal Vincular cuenta (cc#209).
 *
 * Responde, en este orden, lo que el usuario del caso no sabía y lo mandó a soporte:
 * 1. de dónde sale la lista: las conexiones del canal, cada una con la persona que la autorizó;
 * 2. por qué falta la cuenta, con la trampa de cada plataforma (en Meta, «control total» no alcanza);
 * 3. qué hacer: asignársela a esa persona (o copiar las instrucciones para quien pueda), o conectar
 *    con su propio usuario;
 * 4. y, hecho eso, buscar las cuentas nuevas en el momento en vez de esperar la revisión periódica.
 *
 * `open` lo maneja el modal: la abre sola cuando una búsqueda no encuentra nada.
 */
export function MissingAccountHelp({channel, channelName, query, queryMatches, open, onToggle, onDiscovered}) {
    const {t, i18n} = useTranslation();
    const language = i18n && i18n.language;
    // `null` = sin pedir todavía o cargando; un array = las conexiones; `'error'` = no se pudo leer.
    const [connections, setConnections] = useState(null);
    const [copy, setCopy] = useState({state: 'idle', text: ''});
    const requested = useRef(false);
    const discovery = useAccountDiscovery(channel, onDiscovered);
    const tOpts = {channel: channelName, interpolation: RAW};

    // Las conexiones se piden UNA vez y recién al abrir: la mayoría elige su cuenta sin abrir esto.
    useEffect(() => {
        if (!open || requested.current) return undefined;
        requested.current = true;
        let alive = true;
        new APIClient(`${API_URL}agency/connections/`).call('GET').then((response) => {
            if (!alive) return;
            setConnections(isOk(response) && response.data ? connectionsOfChannel(response.data, channel) : 'error');
        }).catch(() => alive && setConnections('error'));
        return () => { alive = false; };
    }, [open, channel]);

    const list = Array.isArray(connections) ? connections : [];
    const person = personText(t, list);

    const copyInstructions = () => {
        const text = instructionsFor(t, channel, {query, person});
        const fallback = () => setCopy({state: 'manual', text});
        try {
            if (!navigator.clipboard || !navigator.clipboard.writeText) {
                fallback();
                return;
            }
            navigator.clipboard.writeText(text).then(() => setCopy({state: 'copied', text}), fallback);
        } catch (error) {
            fallback();
        }
    };

    const discoveryText = () => {
        const {state} = discovery;
        switch (state.kind) {
            case 'running':
                return t(`${NS}.refresh_running`);
            case 'done':
                return queryMatches
                    ? t(`${NS}.refresh_found`, {query: (query || '').trim(), interpolation: RAW})
                    : t(`${NS}.refresh_done`);
            case 'rate_limited':
                return t(`${NS}.refresh_rate_limited`, {count: state.minutes});
            case 'error':
                return t(`${NS}.refresh_error`);
            default:
                return null;
        }
    };
    const statusText = discoveryText();
    const helpId = `link-account-missing-${channel}`;

    return (
        <div className="link-account-missing">
            <button type="button" className="link-account-missing__toggle" aria-expanded={open}
                    aria-controls={helpId} onClick={onToggle}>
                {t(`${NS}.toggle`)}
                <span className="link-account-missing__caret" aria-hidden="true">▾</span>
            </button>

            {open && (
                <div className="link-account-missing__body" id={helpId} data-testid="link-account-missing">
                    <p>{t(`${NS}.source`, tOpts)}</p>
                    {list.length > 0 && (
                        <ul className="link-account-missing__connections">
                            {list.map((connection) => {
                                const details = [
                                    platformUserText(t, channel, connection),
                                    connectedByText(t, connection, language),
                                    unitCountText(t, channel, Number(connection.ad_accounts_count) || 0),
                                ].filter(Boolean);
                                return (
                                    <li key={connection.id}>
                                        <strong>{accessLabel(t, connection)}</strong>
                                        {details.map((detail) => (
                                            <span key={detail} className="link-account-missing__detail">{detail}</span>
                                        ))}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <p>{t(`${NS}.why.${channel}`)}</p>

                    <div className="link-account-missing__step">
                        <p className="link-account-missing__fix">
                            {t(`${NS}.fix.${channel}`, {person, interpolation: RAW})}
                        </p>
                        <div className="link-account-missing__actions">
                            <button type="button" className="btn -negative" onClick={copyInstructions}>
                                {t(`${NS}.copy`)}
                            </button>
                            {copy.state === 'copied' && (
                                <span className="link-account-missing__note" role="status">{t(`${NS}.copied`)}</span>
                            )}
                        </div>
                        {copy.state === 'manual' && (
                            <>
                                <p className="link-account-missing__note">{t(`${NS}.copy_manual`)}</p>
                                <textarea className="link-account-missing__manual" readOnly value={copy.text}
                                          onFocus={(event) => event.target.select()} autoFocus/>
                            </>
                        )}
                    </div>

                    <div className="link-account-missing__step">
                        <p className="link-account-missing__note">{t(`${NS}.refresh_hint`)}</p>
                        <div className="link-account-missing__actions">
                            <button type="button" className="btn -alt" onClick={discovery.start}
                                    disabled={discovery.state.kind === 'running'}
                                    aria-busy={discovery.state.kind === 'running' || undefined}>
                                {t(`${NS}.refresh`)}
                            </button>
                        </div>
                        {statusText && (
                            <p className="link-account-missing__status" role="status">{statusText}</p>
                        )}
                    </div>

                    <p className="link-account-missing__other">
                        {t(`${NS}.other_way`)}{' '}
                        <a href={`/agency/conections?add=${encodeURIComponent(channel)}`} target="_blank"
                           rel="noopener noreferrer">
                            {t(`${NS}.connect_own`, tOpts)} &rarr;
                        </a>
                        <br/>
                        <span className="link-account-missing__note">{t(`${NS}.connect_own_note`)}</span>
                    </p>

                    <p className="link-account-missing__foot">
                        {t(`${NS}.lost_access`)}{' '}
                        <a href={accountsPathFor(channel)} target="_blank" rel="noopener noreferrer">
                            {t(`${NS}.see_accounts`)}
                        </a>
                    </p>
                    <p className="link-account-missing__foot">
                        <LinkToArticle article={HELP_ACCOUNT_NOT_LISTED} text={t(`${NS}.help`)} plain/>
                    </p>
                </div>
            )}
        </div>
    );
}

export default MissingAccountHelp;
